/**
 * Single authoritative tenant branding resolver.
 *
 * Raw restaurant config fields:
 *   theme_color
 *   primary_button_color
 *   secondary_button_color
 *
 * Inheritance (nullish only):
 *   effectivePrimary   = primary_button_color   ?? theme_color
 *   effectiveSecondary = secondary_button_color ?? theme_color
 *
 * A present override — even invalid — does NOT inherit theme_color.
 * Filled CTA tokens fail closed to null (platform overlay) for invalid /
 * missing hex. Valid `#RRGGBB` is admitted with branding-white foreground
 * (contrast vs white is advisory, not an admission gate). Outlined secondary
 * tokens additionally require the fill as ink on light paper (4.5:1 vs white).
 * Theme chrome fails closed to PLATFORM_PRIMARY via resolveAccessiblePrimary.
 * Selection tokens fail closed to null (platform `tokens.selection`).
 * Surface tokens fail closed to null using the same Theme Color admission
 * as selection (custom chrome identity, not PLATFORM_PRIMARY coincidence).
 *
 * Accessibility: Theme chrome / outlined secondary keep 4.5:1 via
 * resolveAccessiblePrimary and resolveTenantOutlinedActionTokens.
 * Filled primary admission does not use a contrast gate.
 */

import { alpha } from "@mui/material";
import { accessibleForegroundOn } from "@/theme/accessible-on-fill";
import type { SelectionPaletteTokens } from "@/theme/create-theme";
import {
  isValidHexColor,
  PLATFORM_PRIMARY,
  resolveAccessiblePrimary,
} from "@/theme/tenant-branding";
import {
  resolveTenantActionTokens,
  resolveTenantOutlinedActionTokens,
  type TenantActionTokens,
} from "@/theme/tenant-cta-sx";

export type RawTenantBranding = {
  theme_color?: string | null;
  primary_button_color?: string | null;
  secondary_button_color?: string | null;
};

/**
 * Opt-in tenant selection overlay. Same fields as platform
 * `palette.custom.selection` / `tokens.selection` so consumers can write
 * `selectionTokens ?? tokens.selection`. Null keeps the platform ladder.
 */
export type TenantSelectionTokens = SelectionPaletteTokens;

/**
 * Subtle Theme Color atmosphere. Independent of selectionTokens.
 * Light-mode alpha compositing via MUI `alpha` — not lighten/darken, not
 * a second RGB blender, not a contrast resolver.
 *
 * Ladder (adapted from the design-system light-surface bands; tinted is
 * 10% rather than selection's 14% so page-scale atmosphere stays quieter):
 *   surfaceFaint  4%  — broad / page-level wash
 *   surfaceSubtle 6%  — contained low-emphasis surface
 *   surfaceTinted 10% — strongest intentional brand atmosphere
 */
export type TenantSurfaceTokens = {
  surfaceFaint: string;
  surfaceSubtle: string;
  surfaceTinted: string;
};

const SURFACE_FAINT_ALPHA = 0.04;
const SURFACE_SUBTLE_ALPHA = 0.06;
const SURFACE_TINTED_ALPHA = 0.1;

export type ResolvedTenantBranding = {
  themeColor: string;
  themeForeground: string;
  isCustomTheme: boolean;
  primaryTokens: TenantActionTokens | null;
  secondaryTokens: TenantActionTokens | null;
  selectionTokens: TenantSelectionTokens | null;
  surfaceTokens: TenantSurfaceTokens | null;
};

/**
 * Null / undefined override inherits theme. A present string does not inherit.
 */
export function effectiveButtonColor(
  override: string | null | undefined,
  themeColor: string | null | undefined,
): string | null {
  if (override != null) return override;
  return themeColor ?? null;
}

function isCustomChrome(themeColor: string | null | undefined, resolved: string): boolean {
  if (!isValidHexColor(themeColor)) return false;
  const trimmed = themeColor!.trim();
  return resolved.toUpperCase() === trimmed.toUpperCase();
}

/**
 * Light-mode selection ladder copied from `create-theme.ts` (storefront is
 * light-only). Do not invent new percentages. Do not read MUI palette.
 *
 * Null when Theme Color is not a custom accessible fill — including the
 * PLATFORM_PRIMARY coincidence (#E8750C passes identity after fail-closed).
 */
function resolveTenantSelectionTokens(
  themeColor: string,
  isCustomTheme: boolean,
): TenantSelectionTokens | null {
  if (!isCustomTheme) return null;
  if (themeColor.toUpperCase() === PLATFORM_PRIMARY.toUpperCase()) return null;

  return {
    hoverSurface: alpha(themeColor, 0.04),
    surfaceFaint: alpha(themeColor, 0.06),
    surfaceSubtle: alpha(themeColor, 0.08),
    surfaceTinted: alpha(themeColor, 0.14),
    surfaceSolid: themeColor,
    borderSoft: alpha(themeColor, 0.08),
    border: alpha(themeColor, 0.4),
    borderStrong: themeColor,
    text: themeColor,
    textOnSolid: accessibleForegroundOn(themeColor),
    ring: themeColor,
    focusOutline: alpha(themeColor, 0.5),
  };
}

/**
 * Subtle tenant surfaces from admitted custom Theme Color only.
 * Does not read primary/secondary button colors. Does not call
 * resolveTenantActionTokens. Does not share selectionTokens.
 */
function resolveTenantSurfaceTokens(
  themeColor: string,
  isCustomTheme: boolean,
): TenantSurfaceTokens | null {
  if (!isCustomTheme) return null;
  if (themeColor.toUpperCase() === PLATFORM_PRIMARY.toUpperCase()) return null;

  return {
    surfaceFaint: alpha(themeColor, SURFACE_FAINT_ALPHA),
    surfaceSubtle: alpha(themeColor, SURFACE_SUBTLE_ALPHA),
    surfaceTinted: alpha(themeColor, SURFACE_TINTED_ALPHA),
  };
}

export function resolveTenantBranding(
  raw: RawTenantBranding | null | undefined,
): ResolvedTenantBranding {
  const themeRaw = raw?.theme_color ?? null;
  const themeColor = resolveAccessiblePrimary(themeRaw);
  const isCustomTheme = isCustomChrome(themeRaw, themeColor);

  const primaryTokens = resolveTenantActionTokens(
    effectiveButtonColor(raw?.primary_button_color, themeRaw),
  );
  const secondaryTokens = resolveTenantOutlinedActionTokens(
    effectiveButtonColor(raw?.secondary_button_color, themeRaw),
  );

  return {
    themeColor,
    themeForeground: accessibleForegroundOn(themeColor),
    isCustomTheme,
    primaryTokens,
    secondaryTokens,
    selectionTokens: resolveTenantSelectionTokens(themeColor, isCustomTheme),
    surfaceTokens: resolveTenantSurfaceTokens(themeColor, isCustomTheme),
  };
}

export function platformTenantBranding(): ResolvedTenantBranding {
  return resolveTenantBranding({
    theme_color: null,
    primary_button_color: null,
    secondary_button_color: null,
  });
}

export { PLATFORM_PRIMARY };
